import { Component } from '@theme/component';

/**
 * DAVIDsTEA gift card balance check.
 *
 * Ported from the old production theme, where the whole thing — markup, CSS and JS — lived in a
 * `custom_liquid` section setting on page.check-gc-balance (9.15 Production - Search). That is
 * editor content rather than code: it cannot be reviewed in a diff, gets no theme check, and a
 * sync from live silently overwrites it. The behaviour below is the same; only its home changed.
 *
 * The lookup itself belongs to Checky, a third-party app: the form posts the last four digits
 * and the shop domain to its endpoint and it answers with the card. Nothing about the card is
 * known to this theme, which is why every field below is rendered from the response rather than
 * from Liquid, and why the endpoint is a section setting — if the merchant ever moves off that
 * app, the URL changes in the editor rather than here.
 *
 * Deliberately not a `<form>` submit: there is no page to navigate to and no Shopify route
 * behind it, so the button drives the request directly and the inputs stay plain fields. Enter
 * still works, because a keydown handler stands in for the submit the form would have done.
 *
 * Every string comes from the locale files by way of data attributes, the same way
 * blocks/dt-bought-together.liquid hands its total label across. The old version carried an
 * inline `{% case %}` block of French and English assigns, which is a second translation file
 * living where no translator would look for it.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} codeInput
 * @property {HTMLInputElement} [nameInput]
 * @property {HTMLButtonElement} submit
 * @property {HTMLElement} error
 * @property {HTMLElement} notFound
 * @property {HTMLElement} results
 * @property {HTMLElement} code
 * @property {HTMLElement} status
 * @property {HTMLElement} balance
 * @property {HTMLElement} deactivated
 * @property {HTMLElement} deactivatedValue
 * @property {HTMLElement} initialAmount
 * @property {HTMLElement} created
 * @property {HTMLElement} expires
 *
 * @extends {Component<Refs>}
 */
class DtGiftCardBalance extends Component {
  requiredRefs = ['codeInput', 'submit', 'error', 'notFound', 'results'];

  /** @type {AbortController | undefined} */
  #abortController;

  /**
   * Enter inside either field runs the check, standing in for the form submit this is not.
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.check();
  }

  async check() {
    const { codeInput, nameInput, submit } = this.refs;
    const strings = this.#strings;

    this.#showError(null);

    const code = codeInput.value.trim();
    if (code.length < 4) {
      this.#showError(strings.errorCode);
      codeInput.focus();
      return;
    }

    const name = nameInput?.value.trim() ?? '';
    if (this.dataset.requireName === 'true' && !name) {
      this.#showError(strings.errorName);
      nameInput?.focus();
      return;
    }

    // A second click while the first request is in flight would race two responses into the
    // same panel; the button is disabled for the duration and the previous request aborted.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const label = submit.textContent;
    submit.disabled = true;
    submit.textContent = strings.checking;

    try {
      const response = await fetch(this.dataset.apiUrl ?? '', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gc_code: code,
          shop: this.dataset.shop,
          ...(name ? { customer: name } : {}),
        }),
        signal: this.#abortController.signal,
      });

      if (!response.ok) throw new Error(`Balance lookup failed with ${response.status}`);

      const content = await response.json();
      this.#render(content?.giftCard);
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return;
      // The old version let a failed lookup fall through to "no gift card found", which tells a
      // shopper their card does not exist when the truth is that the service did not answer.
      console.warn('[dt-gift-card-balance]', error);
      this.#showError(strings.errorRequest);
      this.#hideResults();
    } finally {
      submit.disabled = false;
      submit.textContent = label;
    }
  }

  /** @returns {Record<string, string>} */
  get #strings() {
    return {
      checking: this.dataset.stringChecking ?? '',
      errorCode: this.dataset.stringErrorCode ?? '',
      errorName: this.dataset.stringErrorName ?? '',
      errorRequest: this.dataset.stringErrorRequest ?? '',
      enabled: this.dataset.stringEnabled ?? '',
      disabled: this.dataset.stringDisabled ?? '',
      never: this.dataset.stringNever ?? '',
    };
  }

  /** @param {string | null} message */
  #showError(message) {
    const { error } = this.refs;
    error.textContent = message ?? '';
    error.hidden = !message;
  }

  #hideResults() {
    const { results, notFound } = this.refs;
    results.hidden = true;
    notFound.hidden = true;
  }

  /**
   * @param {Record<string, any> | undefined} giftCard
   */
  #render(giftCard) {
    const { results, notFound } = this.refs;

    if (!giftCard?.maskedCode) {
      results.hidden = true;
      notFound.hidden = false;
      return;
    }

    const strings = this.#strings;
    const { code, status, balance, deactivated, deactivatedValue, initialAmount, created, expires } = this.refs;

    code.textContent = giftCard.maskedCode ?? '';

    const isEnabled = giftCard.enabled === true;
    status.textContent = isEnabled ? strings.enabled : strings.disabled;
    status.classList.toggle('dt-gcb__status--enabled', isEnabled);
    status.classList.toggle('dt-gcb__status--disabled', !isEnabled);

    balance.textContent = giftCard.balance ?? '';
    initialAmount.textContent = giftCard.initialValue ?? '';
    created.textContent = this.#formatDate(giftCard.createdAt) ?? '';
    expires.textContent = this.#formatDate(giftCard.expiresOn) ?? strings.never;

    deactivatedValue.textContent = this.#formatDate(giftCard.deactivatedAt) ?? '';
    deactivated.hidden = !giftCard.deactivatedAt;

    notFound.hidden = true;
    results.hidden = false;
  }

  /**
   * Formatted for the storefront's own locale rather than the browser's: a French page printing
   * an American date reads as a bug to the shopper looking at it.
   * @param {string | null | undefined} value
   * @returns {string | null}
   */
  #formatDate(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(this.dataset.locale || undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
}

if (!customElements.get('dt-gift-card-balance')) {
  customElements.define('dt-gift-card-balance', DtGiftCardBalance);
}
